import axios from "axios";
import { useState } from "react";
import { FaEye, FaEyeSlash, FaSave } from "react-icons/fa"; // Icônes pour l'œil
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


const USER_BACKEND_API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL;

if (!USER_BACKEND_API_BASE_URL) {
    console.error("La variable d'environnement REACT_APP_USER_BACKEND_API_URL n'est pas définie !");
}
// ---------------------------------------------------------------------------------


export default function ChangePasswordForm() {
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false); // Pour afficher/cacher l'ancien mot de passe
    const [showNewPassword, setShowNewPassword] = useState(false); // Pour afficher/cacher le nouveau mot de passe
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Pour afficher/cacher la confirmation du mot de passe
    const token = localStorage.getItem("access_token");

    const validatePassword = (password) => {
        const minLength = 8; // Minimum 8 caractères
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/g.test(password); // Vérification du caractère spécial
        const hasUpperCase = /[A-Z]/.test(password); // Vérification des majuscules
        const hasLowerCase = /[a-z]/.test(password); // Vérification des minuscules
        const hasNumber = /\d/.test(password); // Vérification des chiffres

        if (password.length < minLength) {
            return "Le mot de passe doit contenir au moins 8 caractères.";
        }
        if (!hasSpecialChar) {
            return "Le mot de passe doit contenir au moins un caractère spécial.";
        }
        if (!hasUpperCase || !hasLowerCase) {
            return "Le mot de passe doit contenir des lettres majuscules et minuscules.";
        }
        if (!hasNumber) {
            return "Le mot de passe doit contenir au moins un chiffre.";
        }
        return null; // Aucun problème
    };

    const handleChangePassword = async () => {
        if (newPassword !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }

        // Validation du mot de passe
        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        setLoading(true);
        try {
            // Utilisation de la variable d'environnement pour l'URL de l'API
            await axios.put(
                `${USER_BACKEND_API_BASE_URL}/users/change-password`,
                { oldPassword, newPassword, confirmPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Mot de passe modifié avec succès ✅");
            setOldPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setError(null);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du changement de mot de passe ❌");
            setError("Erreur lors du changement de mot de passe.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold text-[#1F3B3B] mb-4">Changer le mot de passe</h2>

            {/* Affichage du message d'erreur */}
            {error && <div className="text-sm text-red-500">{error}</div>}

            {/* Ancien mot de passe */}
            <div className="relative">
                <Input
                    label="Ancien mot de passe"
                    type={showOldPassword ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                />
                <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-3 top-3 text-gray-600 hover:text-gray-900"
                >
                    {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>

            {/* Nouveau mot de passe */}
            <div className="relative">
                <Input
                    label="Nouveau mot de passe"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-3 text-gray-600 hover:text-gray-900"
                >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>

            {/* Indications sous le champ Nouveau mot de passe */}
            <div className="text-sm text-gray-500 mt-2">
                <ul className="list-inside list-disc">
                    <li>Doit contenir au moins 8 caractères</li>
                    <li>Doit inclure au moins un caractère spécial (!, @, #, etc.)</li>
                    <li>Doit contenir des lettres majuscules et minuscules</li>
                    <li>Doit contenir au moins un chiffre</li>
                </ul>
            </div>

            {/* Confirmer le mot de passe */}
            <div className="relative">
                <Input
                    label="Confirmer le mot de passe"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-gray-600 hover:text-gray-900"
                >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>

            {/* Bouton d'enregistrement */}
            <div className="pt-2">
                <button
                    onClick={handleChangePassword}
                    disabled={loading}
                    className="w-full py-2 rounded text-sm flex items-center justify-center"
                    style={{
                        backgroundColor: loading ? "#bed1d0" : "#1D7C6E", // Couleur de fond du bouton
                        color: "white", // Texte en blanc
                    }}
                >
                    <FaSave className="mr-2" /> {/* Icône disquette */}
                    {loading ? "En cours..." : "Changer le mot de passe"}
                </button>
            </div>
        </div>
    );
}

// Composant Input
function Input({ label, value, onChange, type = "text" }) {
    return (
        <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            <input
                type={type}
                value={value}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
        </div>
    );
}
